import api from './api';
import { PaginationParams, ApiResponse, GatewayWhatsapp, GatewayWhatsappFormData, GatewayWhatsappResponse, GatewayWhatsappConnectionTest, GatewayWhatsappStats } from '../types';

export interface CreateGatewayWhatsappRequest {
  nome: string;
  tipo: 'whaticket' | 'kangaroo' | 'local';
  ativo?: boolean;
  is_default?: boolean;

  // Campos para Whaticket
  token_whaticket?: string;

  // Campos para Kangaroo
  token_kangaroo?: string;

  // Campo para conexao local
  conexao_id?: number;

  // Webhook (opcional para todos)
  webhook_url?: string;

  // Observações
  observacoes?: string;
}

export interface UpdateGatewayWhatsappRequest {
  nome?: string;
  tipo?: 'twilio' | 'whaticket' | 'zenvia' | 'kangaroo' | 'local';
  ativo?: boolean;
  is_default?: boolean;

  // Campos para Whaticket
  token_whaticket?: string;

  // Campos para Kangaroo
  token_kangaroo?: string;

  // Campo para conexao local
  conexao_id?: number;

  // Webhook (opcional para todos)
  webhook_url?: string;

  // Observações
  observacoes?: string;
}

class GatewayWhatsappService {
  /**
   * Lista gateways WhatsApp ativos (para select)
   */
  async getGatewaysWhatsappAtivos(): Promise<GatewayWhatsapp[]> {
    try {
      console.log('🔍 Buscando gateways WhatsApp ativos...');
      const response = await api.get<GatewayWhatsapp[]>('/gateways-whatsapp/select');
      console.log('📡 Resposta da API:', response);
      console.log('📊 Dados dos gateways:', response.data);
      return response.data || [];
    } catch (error) {
      console.error('❌ Erro ao buscar gateways WhatsApp ativos:', error);
      throw error;
    }
  }

  /**
   * Lista todos os gateways WhatsApp com paginação
   */
  async getAllGatewaysWhatsapp(params?: PaginationParams & {
    tipo?: string;
    ativo?: string;
  }): Promise<GatewayWhatsappResponse> {
    try {
      const queryParams = new URLSearchParams();

      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params?.search) queryParams.append('search', params.search);
      if (params?.tipo) queryParams.append('tipo', params.tipo);
      if (params?.ativo) queryParams.append('ativo', params.ativo);

      const url = `/gateways-whatsapp?${queryParams.toString()}`;
      console.log('🔗 Fazendo requisição para:', url);

      const response = await api.get<ApiResponse<GatewayWhatsapp[]> | GatewayWhatsapp[]>(url);

      console.log('📡 Resposta bruta da API:', response.data);

      // Verificar se a resposta é um array direto ou objeto estruturado
      if (Array.isArray(response.data)) {
        const result = {
          gateways: response.data,
          pagination: {
            page: params?.page || 1,
            limit: params?.limit || 10,
            total: response.data.length,
            pages: 1,
          },
        };

        console.log('✅ Resultado processado (array):', result);
        return result;
      }

      // Resposta é objeto estruturado
      const apiResponse = response.data as ApiResponse<GatewayWhatsapp[]>;
      if (apiResponse.success && apiResponse.data) {
        const result = {
          gateways: apiResponse.data,
          pagination: apiResponse.pagination || {
            page: 1,
            limit: 10,
            total: apiResponse.data.length,
            pages: 1,
          },
        };

        console.log('✅ Resultado processado (objeto):', result);
        return result;
      }

      throw new Error(apiResponse.message || 'Erro ao buscar gateways WhatsApp');
    } catch (error: any) {
      console.error('❌ Erro no gatewayWhatsappService.getAllGatewaysWhatsapp:', error);

      if (error.code === 'NETWORK_ERROR' || error.message.includes('timeout')) {
        throw new Error('Erro de conexão com o servidor');
      }

      throw new Error(
        error.response?.data?.message ||
        error.message ||
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Busca gateways WhatsApp ativos para seleção
   */
  async getActiveGatewaysWhatsapp(): Promise<GatewayWhatsapp[]> {
    try {
      const response = await api.get<GatewayWhatsapp[]>('/gateways-whatsapp/active');

      // Acessar response.data que contém os dados reais
      if (response.data && typeof response.data === 'object') {
        // Se tem success e data, significa que é uma resposta estruturada da API
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }

        // Se é um array, é a resposta direta
        if (Array.isArray(response.data)) {
          return response.data;
        }
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao buscar gateways WhatsApp ativos:', error);
      throw error;
    }
  }

  /**
   * Busca um gateway WhatsApp específico por ID
   */
  async getGatewayWhatsappById(id: number): Promise<GatewayWhatsapp> {
    try {
      const response = await api.get<GatewayWhatsapp>(`/gateways-whatsapp/${id}`);

      // Acessar response.data que é o que realmente contém os dados
      if (response.data && typeof response.data === 'object') {
        // Se tem success e data, significa que é uma resposta estruturada da API
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }

        // É a resposta direta
        return response.data;
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao buscar gateway WhatsApp por ID:', error);
      throw error;
    }
  }

  /**
   * Busca gateways WhatsApp por tipo
   */
  async getGatewaysWhatsappByType(tipo: 'twilio' | 'whaticket' | 'zenvia' | 'kangaroo' | 'local'): Promise<GatewayWhatsapp[]> {
    try {
      const response = await api.get<GatewayWhatsapp[]>(`/gateways-whatsapp/tipo/${tipo}`);

      if (response.data && typeof response.data === 'object') {
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }

        if (Array.isArray(response.data)) {
          return response.data;
        }
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao buscar gateways WhatsApp por tipo:', error);
      throw error;
    }
  }

  /**
   * Cria um novo gateway WhatsApp
   */
  async createGatewayWhatsapp(gatewayData: CreateGatewayWhatsappRequest): Promise<GatewayWhatsapp> {
    try {
      console.log('📤 Criando gateway WhatsApp:', gatewayData);

      const response = await api.post<GatewayWhatsapp>('/gateways-whatsapp', gatewayData);

      console.log('✅ Gateway WhatsApp criado:', response.data);

      if (response.data && typeof response.data === 'object') {
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }

        return response.data;
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('❌ Erro ao criar gateway WhatsApp:', error);
      throw error;
    }
  }

  /**
   * Atualiza um gateway WhatsApp
   */
  async updateGatewayWhatsapp(id: number, gatewayData: UpdateGatewayWhatsappRequest): Promise<GatewayWhatsapp> {
    try {
      console.log('📤 Atualizando gateway WhatsApp:', { id, data: gatewayData });

      const response = await api.put<GatewayWhatsapp>(`/gateways-whatsapp/${id}`, gatewayData);

      console.log('✅ Gateway WhatsApp atualizado:', response.data);

      if (response.data && typeof response.data === 'object') {
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }

        return response.data;
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('❌ Erro ao atualizar gateway WhatsApp:', error);
      throw error;
    }
  }

  /**
   * Exclui um gateway WhatsApp
   */
  async deleteGatewayWhatsapp(id: number): Promise<void> {
    try {
      console.log('🗑️ Excluindo gateway WhatsApp:', id);

      await api.delete(`/gateways-whatsapp/${id}`);

      console.log('✅ Gateway WhatsApp excluído');
    } catch (error: any) {
      console.error('❌ Erro ao excluir gateway WhatsApp:', error);
      throw error;
    }
  }

  /**
   * Alterna o status de um gateway WhatsApp
   */
  async toggleGatewayWhatsappStatus(id: number): Promise<GatewayWhatsapp> {
    try {
      console.log('🔄 Alternando status do gateway WhatsApp:', id);

      const response = await api.patch<GatewayWhatsapp>(`/gateways-whatsapp/${id}/toggle-status`);

      console.log('✅ Status do gateway WhatsApp alternado:', response.data);

      if (response.data && typeof response.data === 'object') {
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }

        return response.data;
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('❌ Erro ao alternar status do gateway WhatsApp:', error);
      throw error;
    }
  }

  /**
   * Define um gateway WhatsApp como padrão
   */
  async setGatewayWhatsappAsDefault(id: number): Promise<GatewayWhatsapp> {
    try {
      console.log('⭐ Definindo gateway WhatsApp como padrão:', id);

      const response = await api.patch<GatewayWhatsapp>(`/gateways-whatsapp/${id}/set-default`);

      console.log('✅ Gateway WhatsApp definido como padrão:', response.data);

      if (response.data && typeof response.data === 'object') {
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }

        return response.data;
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('❌ Erro ao definir gateway WhatsApp como padrão:', error);
      throw error;
    }
  }

  /**
   * Testa a conectividade de um gateway WhatsApp
   */
  async testGatewayWhatsappConnection(id: number, phoneNumber: string): Promise<{
    success: boolean;
    message: string;
    data?: any;
    error?: string;
    statusCode?: number;
  }> {
    try {
      const response = await api.post<any>(`/gateways-whatsapp/${id}/test-connection`, { phoneNumber });

      // O interceptor axios retorna response.data do backend
      // O backend envia: { success: true, message: "...", data: {...} }
      const responseData = response as any;

      return {
        success: responseData?.success || false,
        message: responseData?.message || 'Teste concluído',
        data: responseData?.data || responseData,
        error: responseData?.error || null,
        statusCode: responseData?.success ? 200 : 500
      };

    } catch (error: any) {
      console.error('❌ Erro ao testar conectividade do gateway WhatsApp:', error);

      // Extrair informações do erro da API
      const errorResponse = error.response?.data;
      const statusCode = error.response?.status || 500;

      let message = 'Erro ao testar conectividade';
      let errorDetail = null;

      if (errorResponse) {
        message = errorResponse.message || message;
        errorDetail = errorResponse.error || errorResponse.data?.error || null;
      } else if (error.message) {
        message = error.message;
        errorDetail = error.message;
      }

      return {
        success: false,
        message,
        data: null,
        error: errorDetail,
        statusCode
      };
    }
  }

  /**
   * Busca estatísticas dos gateways WhatsApp
   */
  async getGatewayWhatsappStats(): Promise<GatewayWhatsappStats> {
    try {
      const response = await api.get<GatewayWhatsappStats>('/gateways-whatsapp/stats');

      if (response.data && typeof response.data === 'object') {
        if ('success' in response.data && 'data' in response.data && (response.data as any).success) {
          return (response.data as any).data;
        }

        return response.data;
      }

      throw new Error('Formato de resposta inesperado');
    } catch (error: any) {
      console.error('Erro ao buscar estatísticas dos gateways WhatsApp:', error);
      throw error;
    }
  }

  /**
   * Enviar WhatsApp através de um gateway
   */
  async sendWhatsApp(data: {
    contato_id?: number;
    message: string;
    gateway_id?: number;
  }): Promise<{
    success: boolean;
    message: string;
    data?: any;
  }> {
    try {
      const response = await api.post('/gateways-whatsapp/send', data);

      if (response.data && typeof response.data === 'object') {
        // Se tem success, é uma resposta estruturada da API
        if ('success' in response.data) {
          return response.data as {
            success: boolean;
            message: string;
            data?: any;
          };
        }

        // Resposta direta de sucesso
        return {
          success: true,
          message: 'WhatsApp enviado com sucesso',
          data: response.data
        };
      }

      return {
        success: true,
        message: 'WhatsApp enviado com sucesso'
      };
    } catch (error: any) {
      console.error('Erro ao enviar WhatsApp:', error);
      const errorMessage = error.response?.data?.data?.providerResponse?.error ||
        error.response?.data?.message ||
        error.message ||
        'Erro ao enviar WhatsApp';
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  // Métodos utilitários para interface
  getGatewayWhatsappDisplayName(tipo: 'whaticket' | 'kangaroo' | 'local'): string {
    const names = {
      'whaticket': 'Whaticket',
      'kangaroo': 'Kangaroo',
      'local': 'Local (Conexão Nativa)',
    };
    return names[tipo];
  }

  getGatewayWhatsappColor(tipo: 'whaticket' | 'kangaroo' | 'local'): string {
    const colors = {
      'whaticket': '#25D366',
      'kangaroo': '#1976D2',
      'local': '#9C27B0',
    };
    return colors[tipo];
  }

  getGatewayWhatsappIcon(tipo: 'whaticket' | 'kangaroo' | 'local'): string {
    const icons = {
      'whaticket': '💬',
      'kangaroo': '🤖',
      'local': '🔌',
    };
    return icons[tipo];
  }

  getStatusConnectivityColor(status: 'conectado' | 'erro' | 'nao_testado'): string {
    const colors = {
      'conectado': '#4CAF50',
      'erro': '#F44336',
      'nao_testado': '#FF9800',
    };
    return colors[status];
  }

  getStatusConnectivityLabel(status: 'conectado' | 'erro' | 'nao_testado'): string {
    const labels = {
      'conectado': 'Conectado',
      'erro': 'Erro',
      'nao_testado': 'Não Testado',
    };
    return labels[status];
  }
}

export const gatewayWhatsappService = new GatewayWhatsappService();
export default gatewayWhatsappService;
